<template>
  <div id="app" :class="getCustomClass">
    <div v-if="initialLoading && !isReady" class="loader">
      <img :src="require('@/assets/images/loader.svg')"  alt=""/>
    </div>
    <div v-if="!isChatbotEmbedPage" :class="addBuilderStyles">
      <ChatbotEmbedHeader v-if="showChatbotHeader" />
      <Header v-if="showFrame" :is-customer-portal="isCustomerPortal"></Header>
      <div v-if="showBanner" class="banner" :class="{'app__banner': !session.is_legacy}" v-html="sessionBanner"></div>
      <div id="main" :class="{'sticky-header-content': isCustomerPortal}">
        <Sidebar v-if="showFrame"></Sidebar>
        <div v-show="isReady" class="top-background"></div>
        <b-container :class="{
          'builder_styles': session && session.enhanced_mode && !session.use_bootstrap_spacing,
          'order-tracking-container': isOrderTracking
        }">
          <ProcessingGif />
          <Alert v-if="session && !session.is_popup" v-show="isReady && !isLifelineActive && !criticalError"></Alert>
          <router-view v-show="isReady"></router-view>
        </b-container>
        <FloatingButtons />
      </div>
      <Footer v-if="showFrame" v-show="isLanguageReady"></Footer>
      <Lifeline v-show="isLanguageReady && showContactForm"></Lifeline>
      <div ref="translation_detector" style="display:none;">{{translateDetectString}}</div>
    </div>
    <router-view v-if="isChatbotEmbedPage" />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import {TranslateTestString, SessionResult, CustomerPortalInputType, InputType} from './constants'
import {
  getClassFromObject,
  isFirstInputDone,
  setFirstInputDone,
  checkAndApplyFontSize,
  isEmbedded,
  addNewStyles,
  updateFontSizes,
  recalculateFontSizes, setContentWidth, getTrimmedValue
} from './utils'
import cssVars from 'css-vars-ponyfill'
import Header from './components/Header'
import Footer from './components/Footer'
import FloatingButtons from '@/components/FloatingButtons'
import ChatbotEmbedHeader from '@/components/ChatbotEmbedHeader'
import ProcessingGif from '@/components/ProcessingGif'
const Alert = () => import('./components/Alert')
const Sidebar = () => import('./components/Sidebar')
const Lifeline = () => import('./components/Lifeline')

export default {
  name: 'App',
  components: {ProcessingGif, FloatingButtons, Lifeline, Alert, Header, Sidebar, Footer, ChatbotEmbedHeader},
  data: function() {
    return {
      translateDetectString: TranslateTestString,
      lastPoppedEvent: null,
      scrollSpeedFunction: null,
      scrollHeight: 0,
      interval: null
    }
  },
  metaInfo: function() {
    return {
      title: this.campaign ? `${this.campaign.name}` : 'Solvpath Support'
    }
  },
  computed: {
    ...mapState(['initialLoading', 'session', 'campaign', 'isLoading', 'isPopupLoading', 'isLanguageReady', 'styleUrl',
      'faviconUrl', 'criticalError', 'styleVariables', 'style', 'showChatbotHeader', 'isBannerHidden', 'stepStack']),
    ...mapGetters(['canLogoutSession', 'isLifelineActive', 'showContactForm', 'isSessionValid', 'isSessionComplete',
      'isMainMenu', 'isAlreadySessionUrl'
    ]),
    isReady: function() {
      return !this.isLoading && this.isLanguageReady
    },
    showFrame: function() {
      return !isEmbedded()
    },
    showBanner: function() {
      return this.isReady && this.session && this.session.banner && !this.isBannerHidden
    },
    sessionBanner: function() {
      if (this.session && this.session.banner) {
        return updateFontSizes(this.session.banner)
      }
      return null
    },
    getCustomClass: function() {
      if (this.session && (this.session.result === SessionResult.Active)) {
        return getClassFromObject(this.session)
      }

      return ''
    },
    addBuilderStyles: function() {
      if (this.session && this.session.style) {
        addNewStyles('builder_style', this.session.style)
      }
      if (this.campaign && this.campaign.header_content && this.campaign.header_style) {
        addNewStyles('header_style', this.campaign.header_style)
      }
      if (this.campaign && this.campaign.footer_content && this.campaign.footer_style) {
        addNewStyles('footer_style', this.campaign.footer_style)
      }
      return this.isCustomerPortal ? 'cp-main' : ''
    },
    isCustomerPortal: function() {
      return this.session && Object.values(CustomerPortalInputType).indexOf(this.session['step_type']) > -1
    },
    isOrderTracking: function() {
      return this.session && this.session.step_type === InputType.ShipmentTracking
    },
    isChatbotEmbedPage() {
      return this.$route.path === '/chatbot_embed'
    },
    isRetentionLiteLoaderVisible() {
      return this.isLoading || this.isPopupLoading || (this.initialLoading && !this.isReady)
    }
  },
  methods: {
    appendCampaignStyle: function(styleUrl) {
      if (!document.getElementById('campaign_stylesheet')) {
        let file = document.createElement('link')
        file.rel = 'stylesheet'
        file.id = 'campaign_stylesheet'
        file.href = styleUrl
        document.head.appendChild(file)
      }
    },
    appendFavicon: function(faviconUrl) {
      let link = document.getElementById('campaign_favicon')

      if (link) {
        link.href = faviconUrl
      } else {
        link = document.createElement('link')
        link.type = 'image/png'
        link.rel = 'icon'
        link.id = 'campaign_favicon'
        link.href = faviconUrl
        document.head.appendChild(link)
      }
    },
    updateStyleVariables: function(variables) {
      if ('theme_color' in variables) {
        const colorVariables = [
          'button_hover_color',
          'button_active_color',
          'action_color'
        ]

        colorVariables.forEach(key => {
          // set the default color option if it's not specified
          variables[key] = variables[key] || variables['theme_color']
        })

        if (!('button_shadow_color' in variables)) {
          variables['button_shadow_color'] = 'transparent' // default to transparent shadow (i.e. no shadow)
        }
      }

      const marginVariables = [
        'desktop_logo_top_margin',
        'mobile_logo_bottom_margin',
        'mobile_logo_top_margin'
      ]

      marginVariables.forEach(key => {
        // Don't replace == with ===, that's intentional
        // eslint-disable-next-line
        variables[key] = (variables[key] == '0' || variables[key] === undefined) ? '0px' : variables[key]
      })

      cssVars({
        variables: variables,
        include: 'style'
      })
    },
    showLifeline: function() {
      if (this.showContactForm) {
        this.$bvModal.show('contact') // show the lifeline contact form
      } else {
        this.$store.commit('busy')
        this.$router.go(0) // refresh the page to show the lifeline path
      }
    },
    scrollSpeed: function() {
      let newPosition
      let timer
      let delay = 50
      let lastPosition
      let delta
      function clear() {
        lastPosition = null
        delta = 0
      }
      clear()
      return function() {
        newPosition = window.scrollY
        if (lastPosition != null) {
          delta = newPosition - lastPosition
        }
        lastPosition = newPosition
        clearTimeout(timer)
        timer = setTimeout(clear, delay)
        return delta
      }
    },
    observeScrollSpeed: function() {
      if (document.body.classList.contains('mb-device')) {
        if (this.scrollSpeedFunction() < -30) {
          this.$store.commit('exit_popup')
        }
      }
    },
    goHome: function() {
      this.$store.commit('busy')
      this.$store.dispatch('home')
        .then(() => {
          if (this.$router.history.current.meta.requireRedirectForHome) {
            this.$router.replace({name: 'session'}) // If on faq details move to main page
          } else {
            this.$router.go(0) // refresh
          }
        })
    },
    initializeEnhanceContent: function() {
      const _this = this
      // setup main menu
      const elements = Array.from(document.querySelectorAll("[elemtype='main-menu'], [identifier='logo-bg'] img")) || []
      elements.forEach(element => {
        element.addEventListener('click', (e) => {
          _this.goHome()
          return false
        })
      })
    },
    emitRetentionLiteEvent: function(type, payload) {
      if (isEmbedded()) {
        window.parent.postMessage(Object.assign({ type }, payload || {}), '*')
      }
    }
  },
  watch: {
    styleUrl: function(newUrl, oldUrl) {
      if (!oldUrl && newUrl) {
        this.appendCampaignStyle(newUrl)
      }
    },
    faviconUrl: function(newUrl, oldUrl) {
      if (!oldUrl && newUrl) {
        this.appendFavicon(newUrl)
      }
    },
    styleVariables: {
      handler: function(variables, oldVariables) {
        if (variables) {
          this.updateStyleVariables(variables)
        }
      },
      deep: true
    },
    '$store.state.session': function(to, from) {
      if (!this.isLoading) {
        if (from && !to) {
          console.log('session got cleared, logging out')
          if (window.location.pathname !== 'logout') {
            this.$router.replace({name: 'logout'})
          }
        } else {
          setTimeout(() => this.initializeEnhanceContent(), 1000)
        }
      }
    },
    scrollHeight(newHeight) {
      if (isEmbedded()) {
        this.$nextTick(() => {
          window.parent.postMessage({ type: 'iframeHeight', height: newHeight, isMainMenu: this.isMainMenu },
            '*')
        })
      }
    },
    canLogoutSession: function(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        if (isEmbedded()) {
          window.parent.postMessage({ type: 'customerStatus', isloggedIn: this.canLogoutSession }, '*')
        }
      }
    },
    isSessionComplete: function(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.emitRetentionLiteEvent('solvpath_lite_close')
      }
    },
    criticalError: function(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.emitRetentionLiteEvent('solvpath_lite_error')
      }
    },
    isRetentionLiteLoaderVisible: {
      immediate: true,
      handler: function(newVal, oldVal) {
        if (newVal || oldVal !== undefined) {
          this.emitRetentionLiteEvent('solvpath_lite_loading', { loading: newVal })
        }
      }
    }
  },
  created() {
    // get the current language translation
    const lang = this.$cookie.get('googtrans')

    if (lang && lang.length) {
      if (lang[0] === '/') {
        const languages = lang.slice(1).split('/')

        if (languages.length === 2) {
          this.$store.commit('set_language', languages[1])
        }
      }
    }

    if (this.styleUrl) {
      this.appendCampaignStyle(this.styleUrl)
    }

    if (this.faviconUrl) {
      this.appendFavicon(this.faviconUrl)
    }

    if (this.styleVariables) {
      this.updateStyleVariables(this.styleVariables)
    }

    if (this.session && this.session.content_config) {
      // set content widths
      setContentWidth(this.session.content_config)
    }

    var _this = this

    this.$store.commit('set_lifeline_function', function(reason, msg) {
      if (_this.session && !_this.is_lifeline_active && !this.show_lifeline_contact) {
        _this.$store.commit('force_loader_display', true)
        _this.$store.commit('busy')
        _this.$store.dispatch('update_session', {result: reason, message: msg || null})
          .then(session => {
            _this.$store.commit('force_loader_display', false)
            _this.$store.commit('ready')
            console.log({lifeline: reason})
            _this.showLifeline()
          })
          .catch(err => {
            _this.$store.commit('force_loader_display', false)
            _this.$store.commit('ready')
            console.log(err)

            // updating the session state failed - that's a critical error
            _this.$store.commit('lifeline_error', err)
          })
      } else if (_this.criticalError) {
        _this.showLifeline()
      }
    })
    this.$store.dispatch('check_for_ie')
  },
  updated() {
    if (isEmbedded()) {
      this.$nextTick(() => {
        window.parent.postMessage({ type: 'iframeHeight', height: 660, isMainMenu: this.isMainMenu }, '*')
        let modalContentSent = false
        let checkModalInterval = setInterval(() => {
          var modalContent = document.querySelector('.modal-content')
          if (modalContent && !modalContentSent) {
            modalContentSent = true
            window.parent.postMessage({ type: 'modalStatus', modalOpened: true }, '*')
            clearInterval(checkModalInterval)
          }
        }, 100)
        setTimeout(() => {
          clearInterval(checkModalInterval)
        }, 5000)
      })
      this.scrollHeight = document.documentElement.scrollHeight
    }
    setTimeout(() => checkAndApplyFontSize(), 120)
  },
  mounted() {
    this.interval = setInterval(() => {
      this.scrollHeight = document.documentElement.scrollHeight
    }, 100)
    this.$store.dispatch('wait_for_translation', {element: this.$refs['translation_detector'], timeout: 1000})
      .then(value => {
        console.log(value)
        this.$store.commit('language_ready', true)
      })
      .catch(err => {
        console.log(err)
        this.$store.commit('language_ready', true) // not really ready, but can't wait forever
      })
    let _this = this
    this.scrollSpeedFunction = this.scrollSpeed()

    document.addEventListener('touchstart', function() {
      document.body.classList.add('mb-device')
    })
    document.addEventListener('scroll', this.observeScrollSpeed)
    _this.initializeEnhanceContent()
    if (this.isSessionValid && !isFirstInputDone() && !this.isLifelineActive && this.session.can_enter_exit_lifeline) {
      setTimeout(() => {
        this.$router.push({ name: 'undo', params: { step: getTrimmedValue(this.session.step_slug) } })
        this.$router.push({ name: 'session', params: { step: getTrimmedValue(this.session.step_slug) } })
      }, 100)
      setFirstInputDone()
    }
    // apply font for mobile and desktop after DOM is rendered
    document.onreadystatechange = () => {
      if (document.readyState === 'complete') {
        checkAndApplyFontSize()
        if (this.session && !this.session.is_legacy && !this.isCustomerPortal) {
          document.body.classList.add('page-wrapper')
        }
      }
    }
    if (isEmbedded()) {
      window.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'translate') {
          const newLang = event.data.language
          this.$cookie.set('googtrans', `/en/${newLang}`)
          window.location.hash = `googtrans(en|${newLang})`
          window.location.reload()
        } else if (event.data === 'logout') {
          console.log('received logout message from parent')
          this.$router.replace({name: 'logout'})
        } else if (event.data === 'main-menu') {
          console.log('received main-menu message from parent')
          _this.goHome()
        }
      }, false)
    }
    if (isEmbedded() && this.canLogoutSession) {
      window.parent.postMessage({ type: 'customerStatus', isloggedIn: this.canLogoutSession }, '*')
    }

    window.addEventListener('resize', recalculateFontSizes)
    window.addEventListener('load', () => {
      console.log('window loaded')
      recalculateFontSizes()

      if (this.session && !this.session.is_step_preview) {
        let stepStack = this.stepStack

        // rebuild history stack
        if (!this.isAlreadySessionUrl) {
          this.$router.replace({ name: 'session' })
          stepStack = []
        }
        for (let i = 0; i < stepStack.length; i++) {
          history.pushState({step: stepStack[i].slug}, null, `/undo/${stepStack[i].slug}`)
          history.pushState({step: stepStack[i].slug}, null, `/session/${stepStack[i].slug}`)
        }
      }
    })
  },
  beforeDestroy() {
    if (isEmbedded()) {
      clearInterval(this.interval)
    }
    this.$root.$off('toggle-processing-gifs')
  }
}
</script>

<style lang="less">
@import (css) url('https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Ubuntu:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400;1,500;1,700&family=Open+Sans:ital,wght@0,300..800;1,300..800&display=swap');
@import "assets/css/mobile.css";
@import "assets/css/style.less";

</style>
